import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { LEGAL_ENTITY_NAME, LEGAL_JURISDICTION, SUPPORT_EMAIL } from '../../config';

export default function Terms() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="2026-09-30">
      <p>
        These Terms of Service ("Terms") govern your use of the Sheba Smart Home
        website, mobile application, and customer portal (together, the
        "Services"), provided by {LEGAL_ENTITY_NAME}. By creating an account or
        using the Services, you agree to these Terms.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">1. The Services</h2>
        <p>
          Sheba Smart Home provides software and, where applicable, hardware and
          installation services for home automation, device control, camera
          monitoring, and related functionality. Some functionality depends on
          third-party devices, ecosystems, or a local gateway installed at your
          property, and its availability may vary.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">2. Your account</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>You must provide accurate information when registering.</li>
          <li>You are responsible for keeping your password and any device passcode confidential.</li>
          <li>You are responsible for activity that occurs under your account.</li>
          <li>One account is intended for one individual; family/shared-home access is provided through the Services' own family-sharing feature, not by sharing login credentials.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">3. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use the Services for any unlawful purpose.</li>
          <li>Attempt to gain unauthorized access to any account, device, or system that isn't yours.</li>
          <li>Interfere with or disrupt the Services' operation or security.</li>
          <li>Reverse-engineer the Services except where permitted by law.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">4. Purchases (Shop)</h2>
        <p>
          Where the Shop is available, product descriptions, pricing, and
          availability are as shown at the time of purchase. Products may be
          fulfilled directly by Sheba or by a third-party supplier, as indicated
          on the product listing. Shipping times, warranty terms, and return
          policies are specified per product or product category.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">5. Third-party devices and ecosystems</h2>
        <p>
          The Services can integrate with third-party smart-home devices and
          ecosystems (for example Zigbee, Matter, or Tuya-compatible devices).
          We are not responsible for the availability, performance, or security
          of third-party devices or services, which are governed by their own
          terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">6. Service availability</h2>
        <p>
          We aim to keep the Services available and reliable but do not
          guarantee uninterrupted access. Some features (for example, remote
          camera access or cloud automation execution) require an internet
          connection and a working connection to our servers.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">7. Liability</h2>
        <p>
          To the maximum extent permitted by law, {LEGAL_ENTITY_NAME} is not
          liable for indirect, incidental, or consequential damages arising from
          your use of the Services, including damages caused by improper
          installation, unauthorized modification, or misuse of connected
          devices. Nothing in these Terms limits liability that cannot be
          limited under applicable law.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">8. Account termination and deletion</h2>
        <p>
          You may delete your account at any time — see{' '}
          <a href="/account-deletion" className="text-[#00d4ff]">Account Deletion</a>. We may
          suspend or terminate accounts that violate these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">9. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. Continued use of the
          Services after an update constitutes acceptance of the revised Terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">10. Governing law</h2>
        <p>These Terms are governed by the laws of {LEGAL_JURISDICTION}.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">11. Contact</h2>
        <p>
          Questions about these Terms: {' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#00d4ff]">{SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
